<script setup lang="ts">
/**
 * Sell SMS prices editor: sms_type_id → { steps, packages } (no aggregator).
 */
import { computed, onMounted, ref, watch } from 'vue';
import { DxTagBox } from 'devextreme-vue/tag-box';
import {
  DxDataGrid,
  DxColumn,
  DxEditing,
} from 'devextreme-vue/data-grid';
import { bottoGridPaging, getMessageTypesLookup } from '@botto/core';
import {
  emptySmsTypeOnlyBillingUi,
  emptySmsTypePrices,
  smsTypeBillingMapToUi,
  uiToSmsTypeBillingMap,
  type SmsTypeOnlyBillingUi,
  type SmsTypePricesUi,
} from '../../api/normalizers';
import type { SmsTypeBillingMap } from '../../api/types';

const { paging, pager } = bottoGridPaging({ pageSize: 10 });

const props = defineProps<{
  modelValue: SmsTypeBillingMap | undefined | null;
}>();
const emit = defineEmits<{
  (e: 'update:modelValue', v: SmsTypeBillingMap): void;
}>();

const ui = ref<SmsTypeOnlyBillingUi>(emptySmsTypeOnlyBillingUi());
const messageTypesLookup = getMessageTypesLookup();
let lastEmittedWireJson = '';
let suppressPush = false;

function normalizeSmsTypeIds(raw: unknown[] | null | undefined): number[] {
  const out: number[] = [];
  for (const value of raw ?? []) {
    const n = typeof value === 'number' ? value : Number(value);
    if (Number.isInteger(n) && n > 0 && !out.includes(n)) out.push(n);
  }
  return out.sort((a, b) => a - b);
}

function sameSmsTypeIds(a: number[], b: number[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((id, i) => id === b[i]);
}

onMounted(() => {
  void messageTypesLookup.ensureLoaded();
});

function syncUiFromWire(map: SmsTypeBillingMap | undefined | null): void {
  const incoming = smsTypeBillingMapToUi(map);
  const selectedTypes = normalizeSmsTypeIds(incoming.selectedTypes);
  const pricesByType: Record<number, SmsTypePricesUi> = {};
  for (const id of selectedTypes) {
    pricesByType[id] = incoming.pricesByType[id] ?? emptySmsTypePrices();
  }

  // Keep locally selected types that are richer than wire (just selected, no prices yet).
  const wireTypes = new Set(selectedTypes);
  for (const id of normalizeSmsTypeIds(ui.value.selectedTypes)) {
    if (!wireTypes.has(id)) {
      selectedTypes.push(id);
      pricesByType[id] = ui.value.pricesByType[id] ?? emptySmsTypePrices();
    }
  }
  selectedTypes.sort((a, b) => a - b);

  suppressPush = true;
  ui.value = { selectedTypes, pricesByType };
  suppressPush = false;
}

watch(
  () => JSON.stringify(props.modelValue ?? {}),
  (json) => {
    if (json === lastEmittedWireJson) {
      lastEmittedWireJson = '';
      return;
    }
    syncUiFromWire(JSON.parse(json) as SmsTypeBillingMap);
  },
  { immediate: true },
);

function pushUpdate(): void {
  if (suppressPush) return;
  const wire = uiToSmsTypeBillingMap(ui.value);
  lastEmittedWireJson = JSON.stringify(wire);
  emit('update:modelValue', wire);
}

function ensureTypePrices(smsTypeId: number): SmsTypePricesUi {
  if (!ui.value.pricesByType[smsTypeId]) {
    ui.value.pricesByType[smsTypeId] = emptySmsTypePrices();
  }
  return ui.value.pricesByType[smsTypeId];
}

function onTypesChanged(raw: unknown[] | null | undefined): void {
  const types = normalizeSmsTypeIds(raw);
  if (sameSmsTypeIds(ui.value.selectedTypes, types)) return;

  for (const key of Object.keys(ui.value.pricesByType)) {
    const id = Number(key);
    if (!types.includes(id)) delete ui.value.pricesByType[id];
  }
  for (const smsTypeId of types) {
    ensureTypePrices(smsTypeId);
  }
  ui.value.selectedTypes = types;
  pushUpdate();
}

function typePrices(smsTypeId: number): SmsTypePricesUi | undefined {
  return ui.value.pricesByType[smsTypeId];
}

function onStepsRemoved(smsTypeId: number): void {
  const entry = ui.value.pricesByType[smsTypeId];
  if (entry && entry.steps.length === 0) {
    entry.steps.push({ from: 0, to: null, price: 0 });
  }
  pushUpdate();
}

const messageTypeOptions = computed(() =>
  messageTypesLookup.items.value.map((it) => ({
    value: it.id,
    text: messageTypesLookup.labelOf(it.id),
  })),
);

function messageTypeTitle(id: number): string {
  return messageTypesLookup.labelOf(id) || String(id);
}
</script>

<template>
  <div class="sms-type-only">
    <div class="sto-subsection">
      <div class="sto-subsection-label">Типы SMS</div>
      <DxTagBox
        :value="ui.selectedTypes"
        :items="messageTypeOptions"
        display-expr="text"
        value-expr="value"
        placeholder="Выберите типы SMS"
        :show-selection-controls="true"
        :search-enabled="true"
        @value-changed="(e) => onTypesChanged(e.value)"
      />
    </div>

    <div v-if="!ui.selectedTypes.length" class="sto-empty">
      Выберите типы SMS для настройки цен продажи.
    </div>

    <div
      v-for="smsType in ui.selectedTypes"
      :key="smsType"
      class="sto-type-block"
    >
      <div class="sto-type-title">{{ messageTypeTitle(smsType) }}</div>

      <div class="sto-section">
        <div class="sto-section-title">Степовая тарификация</div>
        <DxDataGrid
          v-if="typePrices(smsType)"
          :data-source="typePrices(smsType)!.steps"
          :show-borders="true"
          :show-row-lines="true"
          :hover-state-enabled="true"
          :column-auto-width="true"
          :paging="paging"
          :pager="pager"
          @row-updated="pushUpdate"
          @row-inserted="pushUpdate"
          @row-removed="onStepsRemoved(smsType)"
        >
          <DxEditing :allow-updating="true" :allow-adding="true" :allow-deleting="true" mode="cell" />
          <DxColumn data-field="from" caption="От" data-type="number" :width="110" />
          <DxColumn data-field="to" caption="До (∞ = пусто)" data-type="number" :width="130" />
          <DxColumn
            data-field="price"
            caption="Цена"
            data-type="number"
            :format="{ type: 'fixedPoint', precision: 4 }"
          />
        </DxDataGrid>
      </div>

      <div class="sto-section">
        <div class="sto-section-title">Пакетная тарификация</div>
        <DxDataGrid
          v-if="typePrices(smsType)"
          :data-source="typePrices(smsType)!.packages"
          :show-borders="true"
          :show-row-lines="true"
          :hover-state-enabled="true"
          :column-auto-width="true"
          :paging="paging"
          :pager="pager"
          @row-updated="pushUpdate"
          @row-inserted="pushUpdate"
          @row-removed="pushUpdate"
        >
          <DxEditing :allow-updating="true" :allow-adding="true" :allow-deleting="true" mode="cell" />
          <DxColumn data-field="from" caption="От" data-type="number" :width="110" />
          <DxColumn data-field="to" caption="До" data-type="number" :width="110" />
          <DxColumn
            data-field="price"
            caption="Цена"
            data-type="number"
            :format="{ type: 'fixedPoint', precision: 4 }"
          />
        </DxDataGrid>
      </div>
    </div>
  </div>
</template>

<style scoped>
.sms-type-only { display: flex; flex-direction: column; gap: 16px; padding-top: 4px; }
.sto-empty { color: #9ca3af; padding: 12px; text-align: center; border: 1px dashed #e5e7eb; border-radius: 4px; }
.sto-type-block { display: flex; flex-direction: column; gap: 12px; padding: 12px; border: 1px solid #e5e7eb; border-radius: 6px; background: #fafafa; }
.sto-type-title { font-weight: 600; font-size: 14px; color: #374151; }
.sto-section { display: flex; flex-direction: column; gap: 8px; }
.sto-subsection { display: flex; flex-direction: column; gap: 6px; }
.sto-subsection-label { font-size: 12px; color: #666; }
.sto-section-title { font-weight: 500; font-size: 13px; color: #4b5563; }
</style>
